import { useBehavior } from '@libs/hooks';
import { selectMutableWidget, updateWidgetPropertyByPath, updateWidgetValue, useAppDispatch, useAppSelector } from '@libs/store';
import { JsonPathEnum, TCommonInputOption, TInputData, TWidget } from '@libs/types';
import { useEffect, useRef, useState } from 'react';
import { TDateInputData } from '../types/data';
import { validateDate } from './dateUtils';

export const useDateInput = (widget: TWidget<TDateInputData, TCommonInputOption>) => {
    const {
        uid,
        internalId,
        data: { mandatory, regex, mask, validationsStates },
    } = widget;
    const input = useAppSelector((state) => selectMutableWidget(state, uid));
    const { isDisabled, isHidden } = useBehavior(widget);
    const inputData = (input?.data || {}) as TInputData;
    const defaultErrorMessage = inputData.messages?.errorMessage || inputData.errorMessage;
    const isFirstFocusRender = useRef(!isDisabled);
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | undefined>(defaultErrorMessage);
    const dispatch = useAppDispatch();
    const valueStr = inputData.value as string;
    const value = valueStr ?? '';
    const [isError, setError] = useState(input?.options?.isError);
    const isFilled = !!value || isFocused;
    const label = widget.data.messages?.label || widget.data.label;
    const helperText = widget.data.messages?.helperText || widget.data.helperText;

    useEffect(() => {
        if (internalId) {
            dispatch(
                updateWidgetPropertyByPath({
                    uid: uid,
                    value: validateDate(value, mask, mandatory, regex, defaultErrorMessage, validationsStates).isValid,
                    path: JsonPathEnum.IS_VALID,
                }),
            );
        }
    }, [dispatch, defaultErrorMessage, internalId, uid, validationsStates, value, mandatory, regex, mask]);

    const getClearValue = (val: string) => {
        return val;
    };

    const handleChange = (value: string) => {
        setError(false);
        dispatch(updateWidgetValue({ value, uid }));
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        if (!isDisabled) {
            const value = getClearValue(e?.target?.value);
            const validationResult = validateDate(value, mask, mandatory, regex, defaultErrorMessage, validationsStates);
            setError(!validationResult.isValid);
            setErrorMessage(validationResult.errorMessage);
            setIsFocused(false);
        }
    };

    const handleFocus = () => {
        if (!isDisabled) {
            setIsFocused(true);
            setError(isFirstFocusRender.current && input?.options?.isError && input?.options?.onFocus);
            isFirstFocusRender.current = false;
        }
    };

    return {
        value,
        isFocused,
        isFilled,
        isError,
        errorMessage,
        label,
        helperText,
        isDisabled,
        isHidden,
        handleChange,
        handleBlur,
        handleFocus,
    };
};
